/* eslint-disable */
import { VideoProcessor } from './core/VideoProcessor'
import type { VideoProcessorConfig } from './core/types'

/**
 * Create a new {@link VideoProcessor} instance with the given config.
 *
 * @param config - Pass `{ adapter: 'opentok' }` for the built-in
 *                 OpenTok adapter, or provide a custom
 *                 {@link IBaseAdapter} implementation.
 * @returns A ready-to-use (but not yet attached) VideoProcessor.
 *
 * @example
 * ```ts
 * const vp = createVideoProcessor({ adapter: 'opentok' })
 * await vp.attach(publisher)
 * ```
 */
export function createVideoProcessor (config: VideoProcessorConfig): VideoProcessor {
  return new VideoProcessor(config)
}

// ─── Core ────────────────────────────────────────────────────────
export { VideoProcessor } from './core/VideoProcessor'
export { EventBus } from './core/EventBus'

// ─── Adapters ────────────────────────────────────────────────────
export { BaseAdapter } from './adapters/BaseAdapter'
export { OpenTokAdapter } from './adapters/OpenTokAdapter'

// ─── Segmentation ────────────────────────────────────────────────
export { MediaPipeSegmenter } from './segmentation/MediaPipeSegmenter'
export { BackgroundCompositor } from './segmentation/BackgroundCompositor'

// ─── Pipeline ────────────────────────────────────────────────────
export { UnifiedTransformer } from './pipeline/UnifiedTransformer'

// ─── Filters & Transformers ──────────────────────────────────────
export { BackgroundManager } from './filters/BackgroundManager'
export { ColorFilterTransformer } from './transformers/ColorFilterTransformer'
export { NSFWTransformer } from './transformers/NSFWTransformer'

// ─── NSFW ────────────────────────────────────────────────────────
export { NSFWDetector } from './nsfw/NSFWDetector'

// ─── Fallbacks ───────────────────────────────────────────────────
export { CanvasNSFWFallback } from './fallback/CanvasNSFWFallback'
export { CSSFilterFallback } from './fallback/CSSFilterFallback'

// ─── Utils ───────────────────────────────────────────────────────
export { detectBrowserSupport, detectBrowser, isBackgroundBlurSupported, hasWebAssembly, hasMediaPipeSupport } from './utils/browser'
export { StatsCollector } from './utils/stats'

// ─── Type re-exports ─────────────────────────────────────────────
export type { NSFWDetectorCallbacks } from './nsfw/NSFWDetector'
export type { StatsWarningCallback } from './utils/stats'

export type {
  VideoProcessorConfig,
  VideoProcessorEvents,
  BackgroundBlurConfig,
  VirtualBackgroundConfig,
  VideoBackgroundConfig,
  ColorFilterConfig,
  NSFWDetectionConfig,
  NSFWDetectionResult,
  NSFWPrediction,
  SupportInfo,
  ProcessorStats,
  ActiveFilter,
  ErrorEventData,
  FilterEventData,
  PerformanceWarningData,
  BackgroundFilterType,
  BlurRadiusLevel,
  BrowserInfo,
  IBaseAdapter,
  MediaPipeSegmenterConfig,
  BackgroundCompositorConfig
} from './core/types'
